import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import type { FeedItem } from '../../domain';

interface Props {
  /**
   * Stories to render. Required: this used to default to a synthetic fixture,
   * which meant the fixture shipped in the bundle and rendered on the live
   * dashboard under real publishers' names. The caller supplies real data or
   * the shelf doesn't render.
   */
  items: FeedItem[];
  /** How many items to show. Default 5. */
  limit?: number;
}

/**
 * Dashboard creative-markets feed — one card per story. Lays out in a
 * responsive row: 1 col on xs, 2 on sm, 3+ on md, capped at `limit` cards.
 *
 * Each card opens its source URL in a new tab. Hero image is optional; cards
 * without `imageUrl` render with a calm title-first layout.
 *
 * Wire to `GET /api/v3/feeds/aggregate` when it exists — pass the result as
 * `items`; no change to this component is needed.
 */
export const StoriesCard: React.FC<Props> = ({ items, limit = 5 }) => {
  const visible = items.slice(0, limit);

  return (
    <Box>
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Latest from creative markets
        </Typography>
        <Chip size="small" variant="outlined" label="preview" sx={{ height: 18 }} />
        <Typography variant="caption" color="text.secondary">
          · Nigerian + African creative + Reddit + AI tools
        </Typography>
      </Stack>

      {visible.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No stories available yet. Check back shortly.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(auto-fill, minmax(260px, 1fr))',
            },
            alignItems: 'stretch',
          }}
        >
          {visible.map(item => (
            <StoryCard key={item.id} item={item} />
          ))}
        </Box>
      )}
    </Box>
  );
};

const StoryCard: React.FC<{ item: FeedItem }> = ({ item }) => {
  const ago = formatRelative(item.publishedAt);
  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <CardActionArea
        component="a"
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
        aria-label={`${item.title} — opens in new tab`}
      >
        {item.imageUrl && (
          <CardMedia
            component="img"
            image={item.imageUrl}
            alt=""
            sx={{ aspectRatio: '16 / 9', objectFit: 'cover' }}
          />
        )}
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {item.source}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              · {ago}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <OpenInNewIcon sx={{ fontSize: 14, opacity: 0.45 }} />
          </Stack>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            sx={{
              lineHeight: 1.3,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {item.title}
          </Typography>
          {item.snippet && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {item.snippet}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

/**
 * Compact relative-time formatter. Uses Intl.RelativeTimeFormat when
 * available; falls back to a static-date string. No external deps.
 */
function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const deltaSec = Math.round((then - Date.now()) / 1000);
  const abs = Math.abs(deltaSec);

  const fmt =
    typeof Intl !== 'undefined' && 'RelativeTimeFormat' in Intl
      ? new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
      : null;

  if (!fmt) {
    return new Date(iso).toLocaleDateString();
  }
  if (abs < 60) return fmt.format(deltaSec, 'second');
  if (abs < 3600) return fmt.format(Math.round(deltaSec / 60), 'minute');
  if (abs < 86400) return fmt.format(Math.round(deltaSec / 3600), 'hour');
  if (abs < 604800) return fmt.format(Math.round(deltaSec / 86400), 'day');
  return new Date(iso).toLocaleDateString();
}
